import { ILegalTransaction } from '../../../Interfaces/ILegalTransaction';
import { customFetcher } from '../../../hooks/useFetch';

/**
 * Was der flache `PUT /legaltransaction` für eine Neuzuweisung braucht: die
 * drei Pflicht-IDs des Swagger-Schemas plus die Zuweisung. Das Backend schreibt
 * davon ohnehin nur die Zuweisung (#1339).
 */
export interface IReassignPayload {
  idLegalTransaction: number;
  idLegalTransactionState: number;
  idLegalTransactionType: number;
  idLegalTransactionProgress: number;
  Employee_idUser: number | null;
  Notary_idUser: number;
  idUserGroup: number | null;
}

type UploadReassignment = (payload: IReassignPayload) => Promise<unknown>;

/**
 * Baut aus einem Vorgang, wie `getByUser`/`getByUserGroup` ihn liefern, den
 * Payload für die Neuzuweisung. Die rohe Zeile selbst lässt sich nicht
 * zurückschicken: TINYINT-Felder kommen als 0/1 und scheitern an der
 * Swagger-Prüfung (boolean) mit HTTP 400. `-1` steht im Dialog für
 * „Keine Zuweisung" und wird zu `null`.
 */
export const toReassignPayload = (
  legalTransaction: ILegalTransaction
): IReassignPayload => ({
  idLegalTransaction: legalTransaction.idLegalTransaction,
  idLegalTransactionState: legalTransaction.idLegalTransactionState,
  idLegalTransactionType: legalTransaction.idLegalTransactionType,
  idLegalTransactionProgress: legalTransaction.idLegalTransactionProgress,
  Employee_idUser:
    legalTransaction.Employee_idUser === -1
      ? null
      : legalTransaction.Employee_idUser,
  Notary_idUser: legalTransaction.Notary_idUser,
  idUserGroup:
    legalTransaction.idUserGroup === -1 ? null : legalTransaction.idUserGroup,
});

const putReassignment: UploadReassignment = payload =>
  customFetcher({
    endpoint: '/legaltransaction',
    method: 'PUT',
    body: payload,
  });

/**
 * Speichert die Neuzuweisung aller Vorgänge und liefert `true` nur, wenn jeder
 * einzelne durchging. Jeder Aufruf wertet nur seine eigenen Antworten aus —
 * ein zweiter Versuch nach einem Teilfehler schickt alle Vorgänge erneut, was
 * unschädlich ist, weil nur die Zuweisung geschrieben wird.
 */
export const saveReassignedLegalTransactions = async (
  legalTransactions: ILegalTransaction[],
  upload: UploadReassignment = putReassignment
): Promise<boolean> => {
  const results = await Promise.allSettled(
    legalTransactions.map(legalTransaction =>
      upload(toReassignPayload(legalTransaction))
    )
  );

  return results.every(result => result.status === 'fulfilled');
};
